import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Brain, ShieldCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const VerifyOtp = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyOtp } = useAuth();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const encoded = searchParams.get('e');
    if (!encoded) {
      navigate('/register');
      return;
    }
    try {
      setEmail(decodeURIComponent(atob(encoded)));
    } catch {
      navigate('/register');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await verifyOtp({ email, otp });
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="w-full max-w-md px-6 pt-10 pb-8 bg-gray-800 shadow-xl rounded-2xl space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
              <Brain size={40} className="text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            Verify Your Email
          </h2>
          <p className="mt-2 text-gray-400">
            Enter the 6-digit code sent to
          </p>
          <p className="text-sm text-blue-400 font-medium mt-1">{email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 border-opacity-30 rounded-xl">
              <div className="flex items-center space-x-2 text-red-500">
                <AlertCircle size={20} />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <ShieldCheck size={20} className="text-gray-500" />
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              required
              className="w-full pl-12 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 text-white text-center text-2xl transition-all"
              style={{ letterSpacing: '0.6em' }}
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </div>

          <p className="text-center text-xs text-gray-500">
            Check your spam or junk folder if you don't see it.
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:opacity-90 disabled:opacity-50 transition-all font-medium"
          >
            {loading ? 'Verifying...' : 'Verify Account'}
          </button>

          <p className="text-center text-gray-400 text-sm">
            <a href="/register" className="text-blue-400 hover:text-blue-300 transition-colors">
              Back to register
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default VerifyOtp;
